import { Injectable } from '@angular/core';
import { QuestionModel } from 'src/app/api/models';
import { QuestionsService, LiveEventsService } from 'src/app/api/services';
import { questionTypeDisplayFn } from 'src/app/app.types';
import { PanelService } from '../panel.service';
import { Panel, PanelEntityType } from '../panel.types';
import { TranslateService } from '@ngx-translate/core';
import { ImportEntity, WizardImports } from '../../list/list.types';
import { MatDialog } from '@angular/material/dialog';
import {
  ImportAiQuestionComponent,
  ImportQuestionType,
} from '../../common/dialogs/import-ai-question/import-ai-question.component';
import { AuthService } from 'src/app/auth/auth.service';
import { validateQuestions } from 'src/app/utility/app.utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of } from 'rxjs';
import {
  AmplitudeSource,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { Router } from '@angular/router';
import _ from 'lodash';

/* TODO: 
  It used in books module which has old import options design.
  so this imports will be removed in future.
*/
export const getEventQuestionImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<QuestionModel>,
  courseContext = false,
  liveEventId?: number,
  allowAiForSingleQuestionGeneration = false,
  trackingService?: TrackingService
): ImportEntity<QuestionModel>[] =>
  [
    allowAiForSingleQuestionGeneration
      ? {
          id: 'chat-gpt',
          title: translator.instant(
            'content.question.description.aiImportTitle'
          ),
          description: translator.instant(
            'content.question.description.aiImportDescription'
          ),
          highlight: true,
          action: {
            icon: 'code',
            click: () => {
              matDialog
                .open<ImportAiQuestionComponent, ImportQuestionType>(
                  ImportAiQuestionComponent,
                  {
                    data: {
                      type: 'event-question',
                    },
                    autoFocus: false,
                    width: '600px',
                    maxHeight: '90vh',
                  }
                )
                .afterClosed()
                .subscribe((r) => {
                  if (r) {
                    matDialog.closeAll();
                    panelService.importChatGPT$.next(true);
                  }
                });
            },
            text: translator.instant(
              'content.question.addMenu.generateQuestions'
            ),
          },
        }
      : null,
    {
      title: translator.instant(
        'content.examQuestion.title.generateQuestionsWithChatGPT'
      ),
      description: translator.instant(
        'content.question.description.generateQuestionWithChatGPT'
      ),
      action: {
        icon: 'code',
        click: () => {
          if (courseContext) {
            panelService.importFromChatGPT(panel);
          } else {
            const callback = () => {
              const queryParams = {
                type: PanelEntityType.EventQuestion,
              };
              panelService.redirectToImportItems(
                `/cms/csv-imports/question/${liveEventId}`,
                queryParams,
                true
              );
            };
            panelService.importFromChatGPT(panel, () => callback());
          }
        },
        text: translator.instant('content.question.addMenu.generateQuestions'),
      },
    },
    {
      title: translator.instant(
        'content.question.title.importExistingQuestions'
      ),
      description: translator.instant(
        'content.question.description.importExistingQuestions'
      ),
      action: {
        icon: 'move_to_inbox',
        click: () => {
          if (!courseContext && liveEventId) {
            const queryParams = {
              parentId: liveEventId,
              type: PanelEntityType.EventQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/import-items`,
              queryParams,
              true
            );
          } else {
            panelService.importItems(panel);
          }
        },
        text: translator.instant('content.question.addMenu.existingQuestions'),
      },
    },
    {
      title: translator.instant('content.question.title.addPollQuestionTitle'),
      description: translator.instant(
        'content.question.description.addPollQuestionTitle'
      ),
      action: {
        icon: 'description',
        click: () => {
          if (!courseContext && liveEventId) {
            trackingService.trackEvent({
              event_type: UIEventEnum.CSV_IMPORT_OPENED,
              event_properties: {
                source: AmplitudeSource.LIVE_EVENT,
              },
            });
            const queryParams = {
              type: PanelEntityType.EventQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/question/${liveEventId}`,
              queryParams,
              true
            );
          } else {
            panelService.importItemAsCSV(panel, AmplitudeSource.LIVE_EVENT);
          }
        },
        text: translator.instant(
          'content.question.addMenu.questionsFromExcelCSV'
        ),
      },
      extra: {
        name: translator.instant('content.question.title.downloadCSV'),
        type: 'link' as 'link',
        icon: 'download',
        click: () => {
          window.open(
            'assets/import-csv-templates/Sample-import-questions.csv',
            '_blank'
          );
        },
      },
    },
    {
      id: 'import-json',
      title: translator.instant(
        'content.question.title.importQuestionFromJSON'
      ),
      description: translator.instant(
        'content.question.description.importQuestionFromJSON'
      ),
      action: {
        icon: 'code',
        click: () => panelService.importItemAsJSON(panel),
        text: translator.instant('content.question.addMenu.questionsFromJSON'),
      },
    },
  ].filter((n) => n);

export const getWizardEventQuestionImports = (
  translator: TranslateService,
  panelService: PanelService,
  matDialog: MatDialog,
  panel: Panel<QuestionModel>,
  courseContext = false,
  queryParams?: { [key: string]: any },
  allowAiForSingleQuestionGeneration = false,
  trackingService?: TrackingService,
  router?: Router
): WizardImports => {
  return {
    createWithAI: {
      text: translator.instant('content.wizardActions.title.createWithAI'),
      icon: 'auto_awesome',
      description: translator.instant(
        'content.wizardActions.description.createQuestionWithAI'
      ),
      tag: translator.instant('content.wizardActions.tag.createWithAI'),
      click: () => {
        let liveEventId = queryParams?.liveEventId;
        let courseId = null;
        if (router.url.includes('courseId')) {
          courseId = router.url.split('courseId=')[1].split('&')[0];
        }
        if (!liveEventId) {
          liveEventId = router.url.split('/live-events/')[1].split('?')[0];
        }
        const callback = () => {
          const queryParams = {
            course: courseId,
            type: PanelEntityType.EventQuestion,
          };
          panelService.redirectToImportItems(
            `/cms/csv-imports/question/${liveEventId}`,
            queryParams,
            true
          );
        };
        panelService.importFromChatGPT(panel, () => callback());
      },
    },
    createButton: {
      text: translator.instant('content.wizardActions.title.createManually'),
      icon: 'edit',
      description: translator.instant(
        'content.wizardActions.description.createManually'
      ),
      click: () => {
        const query = _.cloneDeep(queryParams);
        if (router.url.includes('courseId')) {
          query.courseId = router.url.split('courseId=')[1].split('&')[0];
        }
        matDialog.closeAll();
        router.navigate(['/cms/add/question'], { queryParams: query });
      },
    },
    importButtons: [
      {
        text: translator.instant(
          'content.wizardActions.title.createIndividualQuestionsWithAI'
        ),
        description: translator.instant(
          'content.wizardActions.description.createIndividualQuestionsWithAI'
        ),
        icon: 'auto_awesome',
        hidden: !allowAiForSingleQuestionGeneration,
        click: () => {
          matDialog
            .open<ImportAiQuestionComponent, ImportQuestionType>(
              ImportAiQuestionComponent,
              {
                data: {
                  type: 'event-question',
                },
                autoFocus: false,
                width: '600px',
                maxHeight: '90vh',
              }
            )
            .afterClosed()
            .subscribe((r) => {
              if (r) {
                matDialog.closeAll();
                panelService.importChatGPT$.next(true);
              }
            });
        },
      },
      {
        text: translator.instant(
          'content.wizardActions.title.useExistingQuestions'
        ),
        description: translator.instant(
          'content.wizardActions.description.useExistingQuestions'
        ),
        icon: 'file_copy',
        click: () => {
          const liveEventId = queryParams?.liveEventId;
          if (!courseContext && liveEventId) {
            const queryParams = {
              parentId: liveEventId,
              type: PanelEntityType.EventQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/import-items`,
              queryParams,
              true
            );
          } else {
            panelService.importItems(panel);
          }
        },
      },
      {
        text: translator.instant('content.wizardActions.title.importFromJSON'),
        description: translator.instant(
          'content.wizardActions.description.importFromJSON'
        ),
        icon: 'save_alt',
        click: () => panelService.importItemAsJSON(panel),
      },
      {
        text: translator.instant('content.wizardActions.title.importFromCSV'),
        description: translator.instant(
          'content.wizardActions.description.importFromCSV'
        ),
        icon: 'save_alt',
        click: () => {
          const liveEventId = queryParams?.liveEventId;
          if (!courseContext && liveEventId) {
            trackingService.trackEvent({
              event_type: UIEventEnum.CSV_IMPORT_OPENED,
              event_properties: {
                source: AmplitudeSource.LIVE_EVENT,
              },
            });
            const queryParams = {
              type: PanelEntityType.EventQuestion,
            };
            panelService.redirectToImportItems(
              `/cms/csv-imports/question/${liveEventId}`,
              queryParams,
              true
            );
          } else {
            panelService.importItemAsCSV(panel, AmplitudeSource.LIVE_EVENT);
          }
        },
      },
    ],
  };
};

@Injectable()
export class EventQuestionPanel extends Panel<QuestionModel> {
  constructor(
    panelService: PanelService,
    questions: QuestionsService,
    matDialog: MatDialog,
    events: LiveEventsService,
    translator: TranslateService,
    auth: AuthService,
    matSnackBar: MatSnackBar,
    router: Router
  ) {
    super();
    super.init({
      addMenuItems: [
        {
          icon: 'add',
          onClick: () => panelService.editItem(this),
          text: translator.instant(
            'content.question.addMenu.createNewQuizQuestion'
          ),
        },
      ],
      showChatGptAction: true,
      wizardImports: getWizardEventQuestionImports(
        translator,
        panelService,
        matDialog,
        this,
        true,
        null,
        auth.currentUserHasGroup('Admin') ||
          auth.currentUserHasGroup('ContentManager'),
        null,
        router
      ),
      imports: getEventQuestionImports(
        translator,
        panelService,
        matDialog,
        this,
        true,
        null,
        auth.currentUserHasGroup('Admin') ||
          auth.currentUserHasGroup('ContentManager')
      ),
      changeItemPosition: (questionId, eventId, position) => {
        return events.repositionQuestionInEvent({
          id: eventId,
          question_id: questionId,
          body: { position },
        });
      },
      createItemsFn: (items, parentId) => {
        const sentryInfo = {
          title: `Error while importing questions`,
          file: 'event-question-panel.ts - createItemsFn',
          api: 'copyQuestionToEvent',
          preventLogs: true,
        };
        const { isValid, error } = validateQuestions(
          sentryInfo,
          items,
          translator,
          matSnackBar
        );
        if (!isValid) {
          return of({ error });
        }
        return events.copyQuestionToEvent({
          id: parentId,
          body: items,
        });
      },
      deleteItem: (questionId) => questions.deleteQuestion({ id: questionId }),
      editDialogInput: (question, eventId) => ({
        enableNavigation: true,
        question,
        eventId,
      }),
      fetchItem: (id) => questions.getQuestionById({ id }),
      fetchItems: (eventId, additionalData) =>
        additionalData
          ? events.getQuestionsForEvent({
              id: eventId,
              embed_answers: true,
              embed_tags: true,
            })
          : events.getQuestionsForEvent({
              id: eventId,
            }),
      headerIcon: 'question_answers',
      itemActions: [
        {
          icon: 'edit',
          label: translator.instant('common.tooltip.edit'),
          onClick: (question) => panelService.editItem(this, question),
        },
        {
          icon: 'code',
          label: translator.instant('content.question.actionMenu.downloadJSON'),
          onClick: (item) => panelService.exportItemAsJSON(this, item.id),
        },
        {
          icon: 'link_off',
          label: translator.instant(
            'content.question.actionMenu.removeFromQuiz'
          ),
          disabled: (question) => question['isImported'],
          tooltip: (question) =>
            question['isImported']
              ? translator.instant('content.quiz.notYourQuetion')
              : null,
          onClick: (question) => panelService.unlinkItems(this, question.id),
        },
        {
          icon: 'delete',
          label: translator.instant('content.question.actionMenu.delete'),
          disabled: (question) => question['isImported'],
          tooltip: (question) =>
            question['isImported']
              ? translator.instant('content.quiz.notYourQuetion')
              : null,
          onClick: (question) => panelService.deleteItem(this, question.id),
        },
      ],
      itemSuffixes: (question) => {
        const type = questionTypeDisplayFn(question.type, true) ?? '?';
        const suffixes = [{ text: type, icon: null }];
        return suffixes;
      },
      itemTextProperty: 'text',
      parentKey: 'questions',
      parentType: PanelEntityType.Event,
      selectedItemActions: [
        {
          icon: 'link_off',
          label: translator.instant('content.question.selectedItem.label'),
          onClick: (selectedItemIds) =>
            panelService.unlinkItems(this, selectedItemIds),
        },
      ],
      translations: {
        addItem: translator.instant('content.question.tooltip'),
        deleteItemError: translator.instant(
          'content.question.deleteItem.error'
        ),
        deleteItemSuccess: translator.instant(
          'content.question.deleteItem.success'
        ),
        deleteItemText: translator.instant('content.question.deleteItem.text'),
        deleteItemTitle: translator.instant(
          'content.question.deleteItem.title'
        ),
        header: translator.instant('common.label.questions'),
        unlinkItemConfirmText: translator.instant(
          'content.question.unlinkItem.text'
        ),
        unlinkItemConfirmTitle: translator.instant(
          'content.question.unlinkItem.title'
        ),
        unlinkItemError: translator.instant(
          'content.question.unlinkItem.error'
        ),
        unlinkItemSuccess: translator.instant(
          'content.question.unlinkItem.success'
        ),
      },
      type: PanelEntityType.EventQuestion,
      unlinkItems: (questionIds, eventId) =>
        events.removeQuestionFromEvent({
          id: eventId,
          question_ids: questionIds.join(','),
        }),
    });
  }
}
